<template>
  <v-dialog
    v-model="dialog"
    persistent
    width="800px"
    scrollable
    @keydown.esc="dialog = false"
  >
    <card :loading="loading" :disabled="loading" height="80vh">
      <card-title
        large
        closeable
        class="flex-grow-0"
        @click:close="dialog = false"
      >
        {{ formTitle }}
      </card-title>
      <card-content id="role-card">
        <validation-observer ref="validator">
          <v-row dense>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                mode="eager"
                name="Название"
              >
                <v-text-field
                  :id="`form-text-field-title`"
                  v-model="role.title"
                  outlined
                  dense
                  maxlength="100"
                  :readonly="readonly"
                  placeholder="Введите название роли"
                  :error-messages="errors"
                >
                  <template #label>
                    <req-label>Название</req-label>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </validation-observer>
        <crud-permission-grid
          v-model="selectedPermissions"
          :height-offset="readonly ? 456 : 480"
          :readonly="readonly"
          :permission-tree="permissionTree"
        />
      </card-content>
      <card-actions>
        <v-spacer />
        <v-btn
          id="form-btn-cancel"
          color="primary"
          depressed
          outlined
          @click="dialog = false"
        >
          {{ readonly ? "Закрыть" : "Отмена" }}
        </v-btn>
        <v-btn
          v-if="!readonly"
          id="form-btn-save"
          color="primary"
          depressed
          :disabled="loading"
          @click="onClickActionBtn"
        >
          Сохранить
        </v-btn>
      </card-actions>
    </card>
  </v-dialog>
</template>
<script>
import { each } from "lodash";
import NotificationMixin from "@/mixins/NotificationMixin";
import CrudPermissionGrid from "./CrudPermissionGrid";
import EventBus from "@/utils/EventBus";
import RoleRepository from "@/api/roles";

export default {
  components: {
    CrudPermissionGrid
  },

  mixins: [NotificationMixin],

  model: {
    prop: "open",
    event: "changeDialog"
  },

  props: {
    roleId: {
      type: Number,
      default: null
    },
    open: {
      type: Boolean,
      default: false
    },
    permissionTree: {
      type: Array,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      selectedPermissions: {
        navs: {},
        realmClassLinks: {}
      },
      role: {
        title: ""
      }
    };
  },

  computed: {
    dialog: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit("changeDialog", value);
      }
    },

    isEditMode() {
      return !!this.roleId;
    },

    formAction() {
      const modAction = this.isEditMode ? "edit" : "create";
      return this.readonly ? "view" : modAction;
    },

    formTitle() {
      const modTitle = this.isEditMode
        ? "Редактирование роли"
        : "Создание роли";
      return this.readonly ? "Просмотр" : modTitle;
    }
  },

  watch: {
    open(newV) {
      if (!newV) {
        this.$nextTick(this.resetForm);
      } else if (this.isEditMode) {
        this.loadRole();
      }
    }
  },

  beforeDestroy() {
    this.resetForm();
    EventBus.$off("select-permission-column");
  },

  methods: {
    onClickActionBtn() {
      this.isEditMode ? this.updateRole() : this.createRole();
    },

    transformSelected(list) {
      const result = [];
      each(list, (v, id) => {
        if (v) result.push(id);
      });
      return result;
    },

    async createRole() {
      try {
        if (!(await this.$refs.validator.validate())) {
          this.scrollToError();
          return;
        }
        this.loading = true;
        const { role, selectedPermissions } = this;
        const permissionNavs = this.transformSelected(selectedPermissions.navs);
        const permissionRealmClassLinks = this.transformSelected(
          selectedPermissions.realmClassLinks
        );
        const payload = { role, permissionNavs, permissionRealmClassLinks };
        const { data } = await RoleRepository.createRole(payload);
        this.sendAlert(data.alert);
        this.resetForm();
        EventBus.$emit("permissionsUpdated");
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },

    async updateRole() {
      try {
        if (!(await this.$refs.validator.validate())) {
          this.scrollToError();
          return;
        }
        this.loading = true;
        const { role, selectedPermissions, roleId } = this;
        const permissionNavs = this.transformSelected(selectedPermissions.navs);
        const permissionRealmClassLinks = this.transformSelected(
          selectedPermissions.realmClassLinks
        );
        const payload = { role, permissionNavs, permissionRealmClassLinks };
        const { data } = await RoleRepository.updateRole(payload, roleId);
        this.sendAlert(data.alert);
        this.resetForm();
        this.checkOwnRole();
        EventBus.$emit("permissionsUpdated");
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },

    async loadRole() {
      try {
        this.loading = true;
        const { roleId } = this;
        const { data } = await RoleRepository.getRole(roleId);
        const { permissionNavs, permissionRealmClassLinks, role } = data;
        permissionNavs.forEach(i =>
          this.$set(this.selectedPermissions.navs, i, true)
        );
        permissionRealmClassLinks.forEach(i =>
          this.$set(this.selectedPermissions.realmClassLinks, i, true)
        );
        this.role = role;
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },

    async checkOwnRole() {
      const ownRoles = this.$store.getters.getRoles;
      if (ownRoles.some(role => role.id === this.roleId)) {
        await this.$store.dispatch("fetchProfile");
      }
    },

    resetForm() {
      this.dialog = false;
      this.role = {
        title: ""
      };
      this.selectedPermissions = {
        navs: {},
        realmClassLinks: {}
      };
      this.$emit("update:roleId", null);
      if (this.$refs.validator) this.$refs.validator.reset();
    },

    scrollToError() {
      const wrapper = document.getElementById("role-card");
      const error = document
        .getElementsByClassName("v-input error--text")
        .item(0);
      if (wrapper && error) this.$vuetify.goTo(error, { container: wrapper });
    }
  }
};
</script>
